import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, User, FileText } from "@phosphor-icons/react";

export default function Assessment() {
  const [steps, setSteps] = useState([]);
  const [done, setDone] = useState([]);

  useEffect(() => {
    api.get("/assessment/steps").then((r) => setSteps(r.data));
    api.get("/progress/mine").then((r) => setDone(r.data.sections_completed));
  }, []);

  const mark = async (n) => {
    const key = `assessment:step-${n}`;
    await api.post("/progress/mark", { section: key });
    setDone((d) => [...new Set([...d, key])]);
    toast.success(`Step ${n} marked complete`);
  };

  return (
    <div className="space-y-10">
      <header className="fade-up">
        <div className="overline mb-3">3rd-Party AI Assessment</div>
        <h1 className="font-display text-5xl lg:text-6xl tracking-tighter leading-[0.95]">Intake to Monitoring.<br/>The 9-step map.</h1>
        <p className="text-muted-foreground mt-4 max-w-2xl">
          The exact workflow real AI governance teams follow when evaluating a third-party AI system — with owners, activities and artifacts.
        </p>
      </header>

      <ol className="border-t border-l border-border">
        {steps.map((s) => {
          const key = `assessment:step-${s.step}`;
          const isDone = done.includes(key);
          return (
            <li key={s.step} className="border-r border-b border-border bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6" data-testid={`assessment-step-${s.step}`}>
              <div className="md:col-span-2">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Step</div>
                <div className="font-display text-6xl leading-none text-primary tabular-nums">{String(s.step).padStart(2, "0")}</div>
              </div>
              <div className="md:col-span-7">
                <h2 className="font-display text-2xl lg:text-3xl tracking-tight mb-2">{s.title}</h2>
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                  <span className="inline-flex items-center gap-1"><User size={12} /> {s.owner}</span>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">{s.purpose}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="overline mb-2">Activities</div>
                    <ul className="text-sm space-y-1.5">
                      {s.activities.map((a) => <li key={a} className="flex gap-2"><span className="text-primary">·</span>{a}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="overline mb-2">Artifacts</div>
                    <ul className="text-sm space-y-1.5">
                      {s.artifacts.map((a) => (
                        <li key={a} className="flex gap-2 items-start"><FileText size={12} weight="duotone" className="text-primary mt-1" />{a}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              <div className="md:col-span-3 flex md:justify-end md:items-start">
                <button onClick={() => mark(s.step)} disabled={isDone}
                  className="flex items-center gap-2 px-4 py-2 text-sm border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:bg-secondary disabled:text-muted-foreground disabled:border-border"
                  data-testid={`assessment-mark-${s.step}`}>
                  <CheckCircle size={14} weight={isDone ? "fill" : "regular"} />
                  {isDone ? "Complete" : "Mark done"}
                </button>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
